import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import queryString from 'query-string'

import {
    ApplyForJob,
    ApplyJobSettings,
    type ApplyJobSettingsV2,
    ApplySuccess,
    CheckEmailResponse,
    JobApplicationResponse,
    PostInfo,
    Posts,
    type PostsFilterAreaValue,
    type PostsFilterCitiesItem,
    type PostsFilterItemValue,
    ScreeningQuestions,
} from '@core/app/types/postsApiType'

import type { ApplicationDetailsV2, PostDataV2, PostInfoV2 } from '@page/post-v2/types'

export const postsApi = createApi({
    reducerPath: 'postsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
            if (customCorporation) headers.set('X-CORP-CONTEXT', customCorporation)

            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    tagTypes: ['JobApplications'],
    endpoints: (builder) => ({
        getPosts: builder.query<
            Posts,
            {
                country?: number[]
                industry?: number[]
                city?: number[]
                jobType?: number[]
                workType: number[]
                office?: number[]
                region?: number[]
                query?: string
                sort?: string
            }
        >({
            query: ({ country, industry, city, jobType, office, region, query, sort }) => ({
                url: `/jobs?${queryString.stringify({ query: query, country: country, city: city, region: region, branch: industry, extent: jobType, department: office, sort: sort }, { arrayFormat: 'bracket' })}`,
                method: 'GET',
            }),
            transformResponse(baseQueryReturnValue: Posts): Posts {
                Object.values(baseQueryReturnValue?.data?.filters?.area || {}).forEach((area: PostsFilterAreaValue) => {
                    area.selected =
                        area.selected ||
                        Object.values(area.regions || {}).some((region: PostsFilterItemValue) => {
                            return (region.selected =
                                region.selected ||
                                Object.values(region.cities || {}).some((city: PostsFilterCitiesItem) => city.selected))
                        })
                })
                // baseQueryReturnValue = {
                //     ...baseQueryReturnValue,
                //     data: {
                //         ...baseQueryReturnValue.data,
                //         filters: {
                //             ...baseQueryReturnValue.data.filters,
                //             area: {
                //                 // 2: baseQueryReturnValue.data.filters.area[2],
                //                 32: baseQueryReturnValue.data.filters.area[32],
                //                 // 2: { ...baseQueryReturnValue.data.filters.area[2], regions: [] },
                //             },
                //         },
                //     },
                // } // TODO: test 1 country only
                return baseQueryReturnValue
            },
        }),
        getPostsByPage: builder.query<
            Posts,
            {
                department?: number[]
                country?: number[]
                region?: number[]
                branch?: number[]
                extent?: number[]
                query?: string
                page?: number
                limit?: number
            }
        >({
            query: (params) => ({
                url: `/jobs?${queryString.stringify(params, { arrayFormat: 'bracket' })}`,
                method: 'GET',
            }),
        }),
        getPostInfo: builder.query<PostInfo, { id: string | number | undefined }>({
            query: ({ id }) => ({
                url: `/job/${id}`,
                method: 'GET',
            }),
        }),
        getPostInfoV2: builder.query<PostInfoV2, { id: string | number | undefined }>({
            query: ({ id }) => ({
                url: `/v2/job/${id}`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),
        getPostApplySettingsV2: builder.query<
            ApplyJobSettingsV2,
            { id: string | number | undefined; candidateId?: number }
        >({
            query: ({ id }) => ({
                url: `/v2/job/${id}/settings`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),
        getApplicationDetailsV2: builder.query<ApplicationDetailsV2, { id: string | number | undefined }>({
            query: ({ id }) => ({
                url: `/profile/application/${id}`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),
        getPostInfoPreview: builder.query<PostInfo, { getParams: string | undefined }>({
            query: ({ getParams }) => ({
                url: `/job/preview${getParams}`,
                method: 'GET',
            }),
        }),
        getPostInfoPreviewV2: builder.query<PostInfoV2, { getParams: string | undefined }>({
            query: ({ getParams }) => ({
                url: `/v2/job/preview${getParams}`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),
        getPostSettings: builder.query<
            ApplyJobSettings,
            { id: string | number; code?: string | null; path?: string | null }
        >({
            query: ({ id, code, path }) => {
                const params = new URLSearchParams()

                if (code) params.set('code', code)
                if (path) params.set('path', path)

                return {
                    // url: `/apply/${id}/settings${code && code.length > 0 ? code : ''}${path && path.length > 0 ? `?path=${path}` : ''}`,
                    url: `/apply/${id}/settings${params.toString() ? `?${params.toString()}` : ''}`,
                    method: 'GET',
                }
            },
        }),
        applyForJob: builder.mutation<ApplySuccess, ApplyForJob>({
            query: (body) => ({
                url: `/job/${body.id}/apply`,
                method: 'POST',
                body,
            }),
        }),
        applyForJobV2: builder.mutation<ApplySuccess, ApplyForJob>({
            query: (body) => ({
                url: `/job/${body.id}/apply?v2`,
                method: 'POST',
                body,
            }),
        }),
        checkEmail: builder.mutation<CheckEmailResponse, { email: string }>({
            query: (body) => ({
                url: `/email/duplicate`,
                method: 'POST',
                body,
            }),
        }),
        setJobApplyCandidate: builder.mutation<
            void,
            {
                id: string
                data: {
                    anonymousApplication?: boolean
                    applicationText?: string | null
                    anonymousReason?: string | null
                    screeningQuestions?: ScreeningQuestions | null
                    'g-recaptcha-response': string
                    attachments?: { base64: string; ext: string; name: string }[]
                    dateOfBirth?:
                        | {
                              day: {
                                  value: string
                                  label: string
                              }
                              month: {
                                  value: string
                                  label: string
                              }
                              year: {
                                  value: string
                                  label: string
                              }
                          }
                        | string
                        | null
                    address?: {
                        label: string
                        address1: string
                        city: string
                        country: string
                        postalCode: string
                    } | null
                    gender?: 0 | 1 | 2 | 3 | 4 | 5 | null
                    mobilePhone?: { prefix: string; number: string } | string | null
                    linkedinUrl?: string | null
                    cv?: { base64: string; ext: string; name: string } | null
                }
            }
        >({
            query: (body) => ({
                url: `/profile/job/${body.id}/apply`,
                method: 'POST',
                body,
            }),
        }),
        getJobApplications: builder.query<JobApplicationResponse, any>({
            query: () => ({
                url: '/profile/application',
                method: 'GET',
            }),
            providesTags: ['JobApplications'],
        }),
        deleteJobApplication: builder.mutation<void, { id: number; reason?: string }>({
            query: ({ id, reason }) => ({
                url: `/profile/application/${id}/delete`,
                method: 'DELETE',
                body: { reason },
            }),
            invalidatesTags: ['JobApplications'],
        }),
    }),
})

export const {
    useGetPostApplySettingsV2Query,
    useGetApplicationDetailsV2Query,
    useGetPostInfoV2Query,
    useGetPostsQuery,
    useGetPostsByPageQuery,
    useGetPostInfoQuery,
    useGetPostInfoPreviewQuery,
    useGetPostSettingsQuery,
    useApplyForJobMutation,
    useApplyForJobV2Mutation,
    useCheckEmailMutation,
    useSetJobApplyCandidateMutation,
    useGetJobApplicationsQuery,
    useDeleteJobApplicationMutation,
    useGetPostInfoPreviewV2Query,
} = postsApi
